<template>
  <div>
    <Dialog
    :stateId="dialogId"
    :maxWidth="dialogWidth"
    :confirmClose="confirmCloseNeeded"
    @close="close">
      <v-card v-if="show" id="bid-wizard">
        <v-card-title class="primary white--text">
          <span>{{ isEdit ? $t('editBid') : $t('createBid') }}</span>
          <v-spacer/>
          <BaseDialogActions
          hideRefresh
          hideFullscreen
          customCancel
          elementId="bid-wizard-actions"
          @close="requestClose"/>
        </v-card-title>

        <v-tabs v-model="tab" class="card-tabs sub-tabs px-4 mt-3">
          <v-tab><v-icon small class="mr-1">mdi-clipboard-text-outline</v-icon>{{ $t('bidDetails') }}</v-tab>
          <v-tooltip bottom color="primary" :disabled="!productsTabDisabled">
            <template #activator="{ on }">
              <div v-on="on" class="d-flex">
                <v-tab :disabled="productsTabDisabled"><v-icon small class="mr-1">mdi-pine-tree</v-icon>{{ $t('products') }}</v-tab>
              </div>
            </template>
            <span class="subtitle-1 white--text">{{ productsTabHint }}</span>
          </v-tooltip>
        </v-tabs>

        <v-card-text class="pt-4 mt-n2">
          <v-row v-if="!loaded" class="my-12" justify="center" align="center">
            <v-progress-circular indeterminate color="primary"/>
          </v-row>
          <v-tabs-items v-else v-model="tab" touchless class="transparent">
            <v-tab-item>
              <v-form ref="headerForm">
                <v-row dense class="mx-n3 mt-n3">
                  <v-col cols="12">
                    <DetailCard :title="$t('bidDetails')" :fillHeight="false" elementClass="mb-3">
                      <v-row dense class="mt-n6">
                        <v-col cols="12" sm="8">
                          <v-text-field
                          v-model="header.name"
                          :label="$t('bidName')"
                          :rules="[rules.required]"
                          :counter="(header.name || '').length >= nameMaxLength ? nameMaxLength : false"
                          :maxlength="nameMaxLength"
                          :autofocus="!isEdit"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <DatePicker
                          :dateLabel="$t('bidDate')"
                          :startDate="header.bidDate"
                          @date-picked="header.bidDate = $event"/>
                        </v-col>
                        <v-col cols="12" sm="8">
                          <AccountAutocomplete
                          :onlyExternal="true"
                          clearable
                          required
                          hideRefresh
                          userSetting="supplierAccountAutocomplete"
                          :title="$t('supplier')"
                          :accountId="header.supplierAccountId"
                          @account-chosen="header.supplierAccountId = $event ? $event.accountId : undefined"/>
                        </v-col>
                        <v-col cols="12" sm="4">
                          <DatePicker
                          :dateLabel="$t('bidExpiration')"
                          :startDate="header.bidExpiration"
                          @date-picked="header.bidExpiration = $event"/>
                        </v-col>
                        <v-col cols="12" sm="10">
                          <AccountAutocomplete
                          :onlyInternal="true"
                          hideRefresh
                          :readonly="bidExists"
                          :title="$t('destination')"
                          :accountId="header.destinationAccountId"
                          :originSpot="header.spot"
                          @account-chosen="onDestinationChosen">
                            <template #item="{item}">
                              <span
                              v-if="item.routeDistanceFromOrigin !== undefined"
                              class="ml-auto pl-2 caption grey--text text--darken-1 text-no-wrap d-flex align-center">
                                <Icon
                                icon="mdi-car"
                                iconColor="grey darken-1"
                                :tooltipText="$t('approximateRouteDistance')"/>
                                {{ $t('approxMilesShort', { distance: item.routeDistanceFromOrigin.toFixed(1) }) }}
                              </span>
                              <span v-else-if="originSpotValid && item.distanceFromOrigin === undefined" class="ml-auto pl-2 d-flex align-center">
                                <Icon
                                icon="mdi-map-marker-off"
                                iconColor="grey darken-1"
                                :tooltipText="$t('receiverLocationNotSet')"/>
                              </span>
                            </template>
                          </AccountAutocomplete>
                        </v-col>
                        <v-col cols="6" sm="2">
                          <v-text-field
                          v-model="header.distance"
                          :label="$t('distance')"
                          color="black"
                          :rules="[rules.positiveIntegerBetweenOrBlank(0, MAX_BID_DISTANCE)]"/>
                        </v-col>
                        <v-col cols="12" class="py-0 d-flex align-baseline flex-wrap">
                          <div class="subtitle-1 text--primary">{{ $t('harvestWindow') }}</div>
                          <div v-if="attachedTractAvailabilityText" class="caption grey--text text--darken-1 ml-3">
                            {{ $t('tractAvailability') }}: {{ attachedTractAvailabilityText }}
                          </div>
                        </v-col>
                        <v-col cols="6" sm="6">
                          <DatePicker
                          :dateLabel="$t('harvestStart')"
                          :startDate="header.harvestStartDate"
                          :isValid="harvestStartError"
                          @date-picked="onHarvestStartPicked"/>
                        </v-col>
                        <v-col cols="6" sm="6">
                          <DatePicker
                          :dateLabel="$t('harvestEnd')"
                          :startDate="header.harvestEndDate"
                          :min="header.harvestStartDate"
                          :isValid="harvestEndError"
                          @date-picked="onHarvestEndPicked"/>
                        </v-col>
                      </v-row>
                    </DetailCard>
                  </v-col>
                </v-row>

                <v-row dense class="mb-n3 mx-n3">
                  <v-col cols="12" md="6">
                    <DetailCard :title="$t('geography')" :elementClass="isAttached ? 'inherited-card' : ''">
                      <template #title.adjacent>
                        <Icon
                        v-if="isAttached && attachedTractName"
                        icon="mdi-crosshairs-gps"
                        iconColor="info"
                        :tooltipText="attachedTractName"/>
                        <Icon
                        v-else
                        icon="mdi-map-marker"
                        iconColor="primary"
                        tabindex="0"
                        elementId="bid-location-pick-on-map"
                        :tooltipText="$t('pickOnMap')"
                        @icon-clicked="$refs.locationForm && $refs.locationForm.openMap()"/>
                      </template>
                      <BidLocationDisplay v-if="isAttached" :spot="header.spot || {}"/>
                      <BidLocationForm v-else-if="header.spot" ref="locationForm" :spot="header.spot" @location-changed="header.spot = $event"/>
                    </DetailCard>
                  </v-col>
                  <v-col cols="12" md="6">
                    <DetailCard :title="$t('properties')" :elementClass="isAttached ? 'inherited-card' : ''">
                      <template #title.adjacent>
                        <Icon
                        v-if="isAttached && attachedTractName"
                        icon="mdi-crosshairs-gps"
                        iconColor="info"
                        :tooltipText="attachedTractName"/>
                      </template>
                      <template v-if="isAttached">
                        <v-row no-gutters>
                          <v-col cols="6" sm="5">{{ $t('timberCategory') }}:</v-col>
                          <v-col><span class="text--secondary">{{ timberCategoryName || $t('notSpecified') }}</span></v-col>
                        </v-row>
                        <v-row no-gutters>
                          <v-col cols="6" sm="5">{{ $t('conversion') }}:</v-col>
                          <v-col><span class="text--secondary">{{ conversionName || $t('notSpecified') }}</span></v-col>
                        </v-row>
                        <v-row no-gutters>
                          <v-col cols="6" sm="5">{{ $t('landownerType') }}:</v-col>
                          <v-col><span class="text--secondary">{{ landownerTypeName || $t('notSpecified') }}</span></v-col>
                        </v-row>
                      </template>
                      <v-row dense v-else>
                        <v-col cols="12">
                          <v-autocomplete
                          clearable
                          dense
                          hide-details="auto"
                          :items="allTimberCategories"
                          item-text="name"
                          item-value="timberCategoryId"
                          :label="$t('timberCategory')"
                          color="black"
                          item-color="primary"
                          v-model="header.timberCategoryId"
                          id="bid-timber-category-autocomplete"/>
                        </v-col>
                        <v-col cols="12">
                          <v-autocomplete
                          clearable
                          dense
                          hide-details="auto"
                          :items="allConversions"
                          item-text="name"
                          item-value="conversionId"
                          :label="$t('conversion')"
                          color="black"
                          item-color="primary"
                          v-model="header.conversionId"
                          id="bid-conversion-autocomplete"/>
                        </v-col>
                        <v-col cols="12">
                          <v-autocomplete
                          clearable
                          dense
                          hide-details="auto"
                          :items="allLandownerTypes"
                          item-text="name"
                          item-value="landownerTypeId"
                          :label="$t('landownerType')"
                          color="black"
                          item-color="primary"
                          v-model="header.landownerTypeId"
                          id="bid-landowner-type-autocomplete"/>
                        </v-col>
                      </v-row>
                    </DetailCard>
                  </v-col>
                </v-row>
              </v-form>
            </v-tab-item>

            <v-tab-item>
              <v-card outlined class="mt-n2">
                <v-card-text class="pt-2 px-3 pb-2">
                  <div class="d-flex align-center">
                    <span class="subtitle-1 mr-1">{{ $t('products') }}</span>
                    <Icon
                    icon="mdi-plus"
                    iconColor="success"
                    :small="false"
                    dataTestId="bid-wizard-add-product"
                    :tooltipText="$t('addProduct')"
                    @icon-clicked="openProductDialog()"/>
                    <Icon
                    v-if="usesCostTypes && !isEdit"
                    icon="mdi-cash-multiple"
                    :iconColor="defaultCostComponents.length > 0 ? 'primary' : 'grey darken-1'"
                    :small="false"
                    dataTestId="bid-wizard-default-costs"
                    :tooltipText="$t('defaultBidCosts')"
                    @icon-clicked="defaultCostsDialog = true"/>
                  </div>
                  <NoData v-if="allProducts.length === 0" class="my-4">{{ $t('noProductsYet') }}</NoData>
                  <DataTable
                  v-else
                  hideHeader
                  disablePagination
                  showPointerCursor
                  expandOnRowClick
                  containerClass="pa-0"
                  tableKey="bidProductId"
                  :items="allProducts"
                  :headers="productHeaders"
                  :customCells="productCustomCells"
                  :itemCanExpandFunction="hasCostComponents"
                  :itemClass="productRowClass"
                  @expanded="expandedProducts = $event">
                    <template #productName="{ item }">
                      <v-icon class="mr-1" :style="{ visibility: hasCostComponents(item) ? 'visible' : 'hidden' }">{{ `mdi-chevron-${isExpanded(item) ? 'up' : 'down'}` }}</v-icon>
                      {{ item.productName }}
                    </template>
                    <template #harvestVolume="{ item }">
                      <span class="text-no-wrap">{{ numberWithCommas(item.harvestVolume, 3) }} t</span>
                    </template>
                    <template #deliveredRate="{ item }">
                      <Icon
                      v-if="strictCosts && rateDivergesFromCosts(item)"
                      icon="mdi-alert"
                      iconColor="warning"
                      :tooltipText="$t('bidRateDivergedFromCosts')"/>
                      {{ formatMoney(item.deliveredRate) }}
                    </template>
                    <template #actions="{ item }">
                      <Icon
                      v-if="bidWon"
                      icon="mdi-gavel"
                      :iconColor="item.status === BidProductStatus.Lost.value ? 'error' : 'success'"
                      :tooltipText="item.status === BidProductStatus.Lost.value ? $t('markWon') : $t('markLost')"
                      @icon-clicked="toggleProductStatus(item)"/>
                      <Icon
                      icon="mdi-pencil"
                      iconColor="success"
                      :tooltipText="$t('editProduct')"
                      @icon-clicked="openProductDialog(item)"/>
                      <Icon
                      icon="mdi-delete-forever"
                      iconColor="error"
                      :tooltipText="$t('removeProduct')"
                      @icon-clicked="promptDelete(item)"/>
                    </template>
                    <template #expanded-item="{ item }">
                      <BidCostComponentTable
                      :costComponents="item.costComponents"
                      :hideTotal="!mixedCosts"/>
                    </template>
                  </DataTable>
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>

        <v-card-actions class="px-6 pb-4">
          <v-spacer/>
          <v-btn text @click="requestClose">{{ bidExists ? $t('close') : $t('cancel') }}</v-btn>
          <v-btn
          v-if="tab === 0 && !bidExists"
          color="primary"
          :loading="saving"
          :disabled="!headerSubmittable"
          data-testid="bid-wizard-create"
          @click="createHeader">
            {{ $t('createBid') }}
          </v-btn>
          <v-btn
          v-if="tab === 0 && bidExists"
          color="primary"
          :loading="saving"
          :disabled="!headerSubmittable || !headerDirty"
          data-testid="bid-wizard-save"
          @click="saveHeaderEdits">
            {{ $t('save') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </Dialog>

    <v-dialog v-model="productDialog" width="560" v-if="productDialog">
      <BidProductDialog
      :bidId="bidId"
      :destinationAccountId="header.destinationAccountId"
      :product="productDialogTarget"
      :siblingProducts="allProducts"
      :defaultCostComponents="defaultCostComponents"
      :costsLocked="bidStatusSet && !!productDialogTarget"
      :mixedCosts="mixedCosts"
      @saved="onProductMutated"
      @close="productDialog = false"/>
    </v-dialog>

    <v-dialog v-model="defaultCostsDialog" width="560" v-if="defaultCostsDialog">
      <BidDefaultCostsDialog
      :costComponents="defaultCostComponents"
      :bidName="header.name"
      :distance="header.distance"
      :guided="guidedCostsPending"
      @saved="defaultCostComponents = $event"
      @close="defaultCostsDialog = false"/>
    </v-dialog>

    <v-dialog v-model="deleting" width="400" v-if="deleting">
      <ConfirmDelete
      :title="$t('product')"
      @delete="confirmDelete"
      @cancel-delete="deleting = false"/>
    </v-dialog>
  </div>
</template>

<script>
import moment from 'moment'
import { cloneDeep, isEqual } from 'lodash'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import fieldRules from '@/utils/rules.js'
import BidHeaders from '@/headers/Bid.js'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { formatMoney, numberWithCommas } from '@/utils/NumericMutations'
import { localToUTC, utcToLocalDate } from '@/utils/DateFormatter.js'
import { BidCostTypeMode, BidProductStatus, Outcome, RfbStatus } from '@/utils/Enumerations'
import { StringConstants } from '@/utils/constants'
import { routeMilesFromOrigin } from '@/utils/map/RouteDistance.js'
import { MAX_BID_DISTANCE, rateDivergesFromCosts } from '@/model/Bid.js'

const normalizeDate = (date) => date ? moment(date).startOf('day').format() : date

export default {
  name: 'BidWizard',

  components: {
    Dialog: () => import('@/components/Dialog.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    NoData: () => import('@/components/core/NoData.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    DatePicker: () => import('@/components/helper/DatePicker.vue'),
    AccountAutocomplete: () => import('@/components/autocomplete/AccountAutocomplete.vue'),
    BidLocationForm: () => import('@/components/bid/BidLocationForm.vue'),
    BidLocationDisplay: () => import('@/components/bid/BidLocationDisplay.vue'),
    BidProductDialog: () => import('@/components/bid/BidProductDialog.vue'),
    BidDefaultCostsDialog: () => import('@/components/bid/BidDefaultCostsDialog.vue'),
    BidCostComponentTable: () => import('@/components/bid/BidCostComponentTable.vue'),
    ConfirmDelete: () => import('@/components/helper/ConfirmDelete.vue')
  },

  data: () => ({
    dialogId: uniqueDialogId('bid-wizard-dialog'),
    show: false,
    seedTract: null,
    tab: 0,
    harvestTouched: false,
    loaded: false,
    bidId: undefined,
    loadRequestId: 0,
    isEdit: false,
    mutated: false,
    rules: fieldRules.rules,
    MAX_BID_DISTANCE,
    nameMaxLength: StringConstants.ExtraLongStringLength,
    header: {
      tractId: undefined,
      name: undefined,
      spot: null,
      destinationAccountId: undefined,
      distance: undefined,
      supplierAccountId: undefined,
      bidDate: undefined,
      bidExpiration: undefined,
      harvestStartDate: undefined,
      harvestEndDate: undefined,
      rfbStatus: undefined,
      outcome: undefined,
      timberCategoryId: undefined,
      conversionId: undefined,
      landownerTypeId: undefined
    },
    products: [],
    originalHeader: null,
    productDialog: false,
    productDialogTarget: null,
    defaultCostsDialog: false,
    defaultCostComponents: [],
    guidedCostsPending: false,
    expandedProducts: [],
    deleting: false,
    deleteTarget: null,
    saving: false,
    productsLoaded: false,
    BidProductStatus
  }),

  computed: {
    ...mapGetters('tract', ['allTracts', 'allTimberCategories', 'allConversions', 'allLandownerTypes', 'allBidCostTypes']),
    ...mapGetters('product', { storeProducts: 'allProducts' }),
    ...mapGetters('user', ['companyInfo']),

    dialogWidth () {
      return '960px'
    },

    bidExists () {
      return !!this.bidId
    },

    productsTabDisabled () {
      return !this.bidExists || this.headerDirty
    },

    productsTabHint () {
      return this.bidExists ? this.$t('saveDetailsToEditProducts') : this.$t('createBidToAddProducts')
    },

    bidWon () {
      return this.header.outcome === Outcome.Won.value
    },

    bidStatusSet () {
      return this.header.rfbStatus !== RfbStatus.Submitted.value
    },

    usesCostTypes () {
      return this.allBidCostTypes.length > 0
    },

    mixedCosts () {
      return this.companyInfo?.bidCostTypeMode === BidCostTypeMode.Mixed.value
    },

    strictCosts () {
      return this.usesCostTypes && !this.mixedCosts
    },

    headerDirty () {
      return !isEqual(this.headerPayload(), this.originalHeader)
    },

    confirmCloseNeeded () {
      return this.loaded && this.headerDirty
    },

    nestedDialogOpen () {
      return this.productDialog || this.defaultCostsDialog || this.deleting
    },

    attachedTractName () {
      const tract = (this.allTracts || []).find(t => t.tractId === this.header.tractId) ??
        (this.seedTract?.tractId === this.header.tractId ? this.seedTract : null)
      return tract ? (tract.name || tract.code || '') : ''
    },

    attachedTractAvailabilityText () {
      const tract = (this.allTracts || []).find(t => t.tractId === this.header.tractId)
      if (!tract?.availabilityStartDate || !tract?.availabilityEndDate) return undefined
      return `${utcToLocalDate(tract.availabilityStartDate)} - ${utcToLocalDate(tract.availabilityEndDate)}`
    },

    timberCategoryName () {
      return this.allTimberCategories.find(t => t.timberCategoryId === this.header.timberCategoryId)?.name
    },

    conversionName () {
      return this.allConversions.find(c => c.conversionId === this.header.conversionId)?.name
    },

    landownerTypeName () {
      return this.allLandownerTypes.find(l => l.landownerTypeId === this.header.landownerTypeId)?.name
    },

    allProducts () {
      return this.products.map(p => ({
        ...p,
        productName: this.storeProducts.find(sp => sp.productId === p.productId)?.name
      }))
    },

    productHeaders () {
      return BidHeaders.bidProductHeaders()
    },

    productCustomCells () {
      return [
        { slotName: 'productName', value: 'productName' },
        { slotName: 'harvestVolume', value: 'harvestVolume' },
        { slotName: 'deliveredRate', value: 'deliveredRate' },
        { slotName: 'actions', value: 'actions' }
      ]
    },

    isAttached () {
      return !!this.header.tractId
    },

    // 0 is a deliberate saved value, not empty.
    distanceEmpty () {
      return this.header.distance === null || this.header.distance === undefined || this.header.distance === ''
    },

    // Mirrors AccountAutocomplete: with no valid origin it decorates no distances at all.
    originSpotValid () {
      return typeof this.header.spot?.latitude === 'number' && typeof this.header.spot?.longitude === 'number'
    },

    spotComplete () {
      const spot = this.header.spot
      return !!spot &&
        !!spot.countrySubdivision &&
        !!spot.countrySecondarySubdivision &&
        typeof spot.latitude === 'number' &&
        typeof spot.longitude === 'number'
    },

    // Gates the buttons; harvest validity is deliberately excluded so a submit attempt
    // can reveal the deferred harvest errors instead of silently disabling the button.
    headerSubmittable () {
      if (!this.header.name) return false
      if (!this.header.supplierAccountId) return false
      if (!this.header.destinationAccountId) return false
      if (this.rules.positiveIntegerBetweenOrBlank(0, MAX_BID_DISTANCE)(this.header.distance) !== true) return false
      return this.isAttached || this.spotComplete
    },

    headerValid () {
      return this.headerSubmittable &&
        this.harvestStartValidation === false &&
        this.harvestEndValidation === false
    },

    harvestStartValidation () {
      return this.header.harvestStartDate ? false : this.$t('required')
    },

    harvestEndValidation () {
      if (!this.header.harvestEndDate) return this.$t('required')
      if (this.header.harvestStartDate && this.header.harvestEndDate < this.header.harvestStartDate) {
        return this.$t('endDateBeforeStartDate')
      }
      return false
    },

    harvestStartError () {
      return this.harvestTouched ? this.harvestStartValidation : false
    },

    harvestEndError () {
      return this.harvestTouched ? this.harvestEndValidation : false
    }
  },

  watch: {
    // Macrotask restore: a sync restore re-arms the stacked Dialogs' Escape listener mid-keypress.
    nestedDialogOpen (val) {
      if (val) {
        this.setAllowEscapeToClose(false)
      } else {
        setTimeout(() => { if (!this.nestedDialogOpen) this.setAllowEscapeToClose(true) }, 0)
      }
    },

    defaultCostsDialog (open) {
      if (open || !this.guidedCostsPending) return
      this.guidedCostsPending = false
      this.openProductDialog()
    }
  },

  created () {
    Promise.all([
      this.fetchTimberCategories(),
      this.fetchConversions(),
      this.fetchLandownerTypes(),
      this.fetchBidCostTypes()
    ])
  },

  methods: {
    ...mapActions('bid', [
      'fetchBid',
      'createBid',
      'updateBid',
      'updateBidProduct',
      'deleteBidProduct'
    ]),
    ...mapActions('tract', ['fetchTimberCategories', 'fetchConversions', 'fetchLandownerTypes', 'fetchBidCostTypes']),
    ...mapActions('product', ['fetchProducts']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),
    ...mapMutations('dialog', ['setAllowEscapeToClose']),

    formatMoney,
    numberWithCommas,
    rateDivergesFromCosts,

    async ensureProductsLoaded () {
      if (this.productsLoaded) return
      await this.fetchProducts({ includeByProducts: false, includeProduction: true })
      this.productsLoaded = true
    },

    async onDestinationChosen (account) {
      const previousId = this.header.destinationAccountId
      this.header.destinationAccountId = account ? account.accountId : undefined
      if (!account) return

      const destinationChanged = account.accountId !== previousId
      if (!destinationChanged && !this.distanceEmpty) return
      if (destinationChanged) this.header.distance = undefined

      const [miles] = await routeMilesFromOrigin(this.header.spot, [{ latitude: account.receiverSpotLatitude, longitude: account.receiverSpotLongitude }])
      if (this.header.destinationAccountId !== account.accountId || !this.distanceEmpty) return

      const rounded = Math.round(miles)
      if (miles !== undefined && rounded <= MAX_BID_DISTANCE) this.header.distance = String(rounded)
    },

    // The DatePicker re-emits its current value on mount; only a genuine change counts as a touch.
    onHarvestStartPicked (date) {
      if ((normalizeDate(date) ?? null) !== (normalizeDate(this.header.harvestStartDate) ?? null)) this.harvestTouched = true
      this.header.harvestStartDate = date
    },

    onHarvestEndPicked (date) {
      if ((normalizeDate(date) ?? null) !== (normalizeDate(this.header.harvestEndDate) ?? null)) this.harvestTouched = true
      this.header.harvestEndDate = date
    },

    async open ({ tractId, tract, bid } = {}) {
      this.resetState()
      const requestId = ++this.loadRequestId
      this.seedTract = tract ? { tractId, name: tract.name, code: tract.code } : null
      if (tractId) this.header.tractId = tractId

      if (tract && !bid) {
        this.header.timberCategoryId = tract.timberCategory?.timberCategoryId
        this.header.conversionId = tract.conversion?.conversionId
        this.header.landownerTypeId = tract.landownerType?.landownerTypeId
        this.header.supplierAccountId = tract.supplierAccountId
        if (tract.availabilityStartDate) this.header.harvestStartDate = normalizeDate(tract.availabilityStartDate)
        if (tract.availabilityEndDate) this.header.harvestEndDate = normalizeDate(tract.availabilityEndDate)
        if (tract.spot) {
          this.header.spot = {
            latitude: tract.spot.latitude,
            longitude: tract.spot.longitude,
            countrySubdivision: tract.spot.countrySubdivision,
            countrySecondarySubdivision: tract.spot.countrySecondarySubdivision
          }
        }
      }

      this.show = true
      this.openOrUpdateDialog({ id: this.dialogId, width: this.dialogWidth })

      if (bid) {
        // The form mounts only once loaded: the pickers capture their initial value on
        // mount and would re-assert seeded/blank data over the fetched bid.
        try {
          await this.loadBid(bid, requestId)
        } finally {
          if (requestId === this.loadRequestId) this.loaded = true
        }
      } else {
        if (!this.header.tractId && !this.header.spot) {
          this.header.spot = { latitude: undefined, longitude: undefined, countrySubdivision: undefined, countrySecondarySubdivision: undefined }
        }
        // Warm products for the Products tab; the create flow has no loadBid to do it.
        this.ensureProductsLoaded()
        this.snapshotHeader()
        this.loaded = true
      }
    },

    resetState () {
      this.tab = 0
      this.harvestTouched = false
      this.loaded = false
      this.seedTract = null
      this.bidId = undefined
      this.isEdit = false
      this.mutated = false
      this.header = {
        tractId: undefined,
        name: undefined,
        spot: null,
        destinationAccountId: undefined,
        distance: undefined,
        supplierAccountId: undefined,
        bidDate: moment().startOf('day').format(),
        bidExpiration: moment().add(30, 'days').startOf('day').format(),
        harvestStartDate: moment().startOf('day').format(),
        harvestEndDate: moment().startOf('day').format(),
        outcome: undefined,
        reason: undefined,
        timberCategoryId: undefined,
        conversionId: undefined,
        landownerTypeId: undefined
      }
      this.products = []
      this.originalHeader = null
      this.productDialog = false
      this.productDialogTarget = null
      this.defaultCostsDialog = false
      this.defaultCostComponents = []
      this.guidedCostsPending = false
      this.expandedProducts = []
      this.deleting = false
      this.deleteTarget = null
      this.saving = false
    },

    async loadBid (bid, requestId) {
      this.isEdit = true
      this.bidId = bid.bidId
      const full = await this.fetchBid(bid.bidId)
      if (requestId !== this.loadRequestId) return
      this.header = {
        tractId: full.tractId,
        name: full.name,
        spot: full.spot,
        destinationAccountId: full.destinationAccountId,
        distance: full.distance,
        supplierAccountId: full.supplierAccountId,
        bidDate: normalizeDate(full.bidDate),
        bidExpiration: normalizeDate(full.bidExpiration),
        harvestStartDate: normalizeDate(full.harvestStartDate),
        harvestEndDate: normalizeDate(full.harvestEndDate),
        rfbStatus: full.rfbStatus,
        outcome: full.outcome,
        reason: full.reason,
        timberCategoryId: full.timberCategoryId,
        conversionId: full.conversionId,
        landownerTypeId: full.landownerTypeId
      }
      this.products = full.products ?? []
      this.snapshotHeader()
      await this.ensureProductsLoaded()
    },

    snapshotHeader () {
      this.originalHeader = cloneDeep(this.headerPayload())
    },

    async refreshProducts () {
      const full = await this.fetchBid(this.bidId)
      this.products = full.products ?? []
    },

    headerPayload () {
      return {
        tractId: this.header.tractId,
        name: this.header.name,
        spot: this.header.spot,
        destinationAccountId: this.header.destinationAccountId,
        distance: parseInt(this.header.distance) || 0,
        supplierAccountId: this.header.supplierAccountId,
        bidDate: this.header.bidDate ? localToUTC(this.header.bidDate) : null,
        bidExpiration: this.header.bidExpiration ? localToUTC(this.header.bidExpiration) : null,
        harvestStartDate: this.header.harvestStartDate ? localToUTC(this.header.harvestStartDate) : null,
        harvestEndDate: this.header.harvestEndDate ? localToUTC(this.header.harvestEndDate) : null,
        rfbStatus: this.header.rfbStatus,
        outcome: this.header.outcome,
        reason: this.header.reason,
        timberCategoryId: this.header.timberCategoryId,
        conversionId: this.header.conversionId,
        landownerTypeId: this.header.landownerTypeId
      }
    },

    revealHarvestErrors () {
      this.harvestTouched = true
      this.setSnackError(this.$t('invalidEntity', { entity: this.$t('harvestWindow') }))
    },

    async createHeader () {
      if (this.saving) return
      if (!this.headerValid) {
        this.revealHarvestErrors()
        return
      }
      this.saving = true
      try {
        const created = await this.createBid({
          tractId: this.header.tractId,
          name: this.header.name,
          spot: this.header.spot,
          destinationAccountId: this.header.destinationAccountId,
          distance: parseInt(this.header.distance) || 0,
          supplierAccountId: this.header.supplierAccountId,
          bidDate: this.header.bidDate ? localToUTC(this.header.bidDate) : null,
          bidExpiration: this.header.bidExpiration ? localToUTC(this.header.bidExpiration) : null,
          harvestStartDate: this.header.harvestStartDate ? localToUTC(this.header.harvestStartDate) : null,
          harvestEndDate: this.header.harvestEndDate ? localToUTC(this.header.harvestEndDate) : null,
          timberCategoryId: this.header.timberCategoryId,
          conversionId: this.header.conversionId,
          landownerTypeId: this.header.landownerTypeId
        })
        this.bidId = created.bidId
        this.header.rfbStatus = created.rfbStatus
        this.mutated = true
        this.snapshotHeader()
        this.setSnack(this.$t('bidCreated'))
        this.tab = 1
        if (this.usesCostTypes) {
          this.guidedCostsPending = true
          this.$nextTick(() => { this.defaultCostsDialog = true })
        } else {
          this.$nextTick(() => { this.openProductDialog() })
        }
      } finally {
        this.saving = false
      }
    },

    async saveHeaderEdits () {
      if (!this.headerDirty || this.saving) return
      if (!this.headerValid) {
        this.revealHarvestErrors()
        return
      }
      this.saving = true
      try {
        await this.updateBid({ bidId: this.bidId, payload: this.headerPayload() })
        this.mutated = true
        this.snapshotHeader()
        this.setSnack(this.$t('bidSaved'))
      } finally {
        this.saving = false
      }
    },

    openProductDialog (product = null) {
      this.productDialogTarget = product
      this.productDialog = true
    },

    async onProductMutated () {
      this.mutated = true
      await this.refreshProducts()
    },

    async toggleProductStatus (product) {
      const markingLost = product.status !== BidProductStatus.Lost.value
      if (markingLost && !this.products.some(p =>
        p.bidProductId !== product.bidProductId && p.status === BidProductStatus.Won.value)) {
        this.setSnack(this.$t('wonBidNeedsAWonProduct'))
        return
      }
      const status = product.status === BidProductStatus.Lost.value
        ? BidProductStatus.Won.value
        : BidProductStatus.Lost.value
      await this.updateBidProduct({
        bidId: this.bidId,
        bidProductId: product.bidProductId,
        payload: {
          productId: product.productId,
          harvestVolume: product.harvestVolume,
          status,
          deliveredRate: product.deliveredRate,
          costComponents: product.costComponents.map(c => ({ bidCostTypeId: c.bidCostTypeId, rate: c.rate }))
        }
      })
      this.mutated = true
      await this.refreshProducts()
    },

    hasCostComponents (product) {
      return product.costComponents.length > 0
    },

    isExpanded (product) {
      return this.expandedProducts.some(p => p.bidProductId === product.bidProductId)
    },

    productRowClass (product) {
      return this.hasCostComponents(product) ? '' : 'cursor-default'
    },

    promptDelete (product) {
      this.deleteTarget = product
      this.deleting = true
    },

    async confirmDelete () {
      const product = this.deleteTarget
      this.deleting = false
      await this.deleteBidProduct({ bidId: this.bidId, bidProductId: product.bidProductId })
      this.mutated = true
      await this.refreshProducts()
    },

    requestClose () {
      this.closeDialogsAtOrAbove(this.dialogId)
    },

    close () {
      if (this.mutated) this.$emit('saved')
      this.productDialog = false
      this.deleting = false
      this.show = false
    }
  }
}
</script>

<style scoped>
.inherited-card {
  background-color: #f5f5f5;
}
/* Custom #selection slots render a bare span that would wrap and grow the field;
   force a single line + ellipsis instead. */
::v-deep .v-select__selections {
  flex-wrap: nowrap;
}
::v-deep .v-select__selections > span,
::v-deep .v-select__selection--comma {
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
</style>
